/**
 * P&L waterfall by statement section:
 * Sales → Land → Build → Soft → Commercial → [Pilotage] → PBT → Tax → Fin → Net
 *
 * Pure SVG. Floating deductions use absolute Y: top = previous balance, bottom = new balance.
 * Expand button opens a fullscreen overlay; Esc / backdrop / X close it.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { ArrowsOut, X } from '@phosphor-icons/react';
import { formatMoney } from '../utils/formatters';

const COLORS = {
  revenue: '#059669',
  deduction: '#e57373',
  subtotal: '#94a3b8',
  loss: '#dc2626',
};

const CHART_INLINE = {
  width: 640,
  height: 260,
  padTop: 22,
  padRight: 12,
  padBottom: 52,
  padLeft: 48,
  labelSize: 7,
  tickSize: 8,
};

const CHART_FULL = {
  width: 1100,
  height: 520,
  padTop: 36,
  padRight: 24,
  padBottom: 72,
  padLeft: 64,
  labelSize: 11,
  tickSize: 11,
};

function formatWaterfallLabel(value) {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const formatted = new Intl.NumberFormat('de-CH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  return n < 0 ? `(${formatted})` : formatted;
}

function formatAxisTick(v) {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(v / 1_000)}k`;
  return `${Math.round(v)}`;
}

function fillForStep(step) {
  if (step.kind === 'start') return COLORS.revenue;
  if (step.kind === 'delta') return COLORS.deduction;
  return step.signed >= 0 ? COLORS.subtotal : COLORS.loss;
}

/**
 * Section waterfall matching the P&L cost rollup.
 * Build + Pilotage are included so the bridge reconciles to PBT
 * (Sales − Land − Build − Soft − Commercial − Pilotage = PBT).
 */
function buildWaterfallSteps(outputs, taxLabel) {
  const sales = Number(outputs.totalRevenue) || 0;
  const land = Number(outputs.totalLandCost) || 0;
  const build = Number(outputs.totalConstructionCost) || 0;
  const soft = Number(outputs.softCosts) || 0;
  const commercial = Number(outputs.commercializationCost) || 0;
  const pilotage = Number(outputs.pilotageCost) || 0;
  const pbt = Number(outputs.profitBeforeTax) || 0;
  const tax = Number(outputs.taxAmount) || 0;
  const fin = Number(outputs.totalFinancingCost) || 0;
  const net = Number(outputs.finalNetProfit) || 0;

  const rawSteps = [
    { name: 'Sales', signed: sales, kind: 'start' },
    { name: 'Land', signed: -land, kind: 'delta' },
    { name: 'Build', signed: -build, kind: 'delta' },
    { name: 'Soft', signed: -soft, kind: 'delta' },
    { name: 'Commercial', signed: -commercial, kind: 'delta' },
  ];

  if (pilotage > 0) {
    rawSteps.push({ name: 'Pilotage', signed: -pilotage, kind: 'delta' });
  }

  rawSteps.push(
    { name: 'PBT', signed: pbt, kind: 'subtotal' },
    { name: taxLabel || 'Tax', signed: -tax, kind: 'delta' },
    { name: 'Fin. Cost', signed: -fin, kind: 'delta' },
    { name: 'Net', signed: net, kind: 'end' },
  );

  const steps = rawSteps.filter(
    (step) => step.kind !== 'delta' || Math.abs(step.signed) > 0,
  );

  let running = 0;

  return steps.map((step) => {
    let bottom;
    let top;

    if (step.kind === 'start' || step.kind === 'subtotal' || step.kind === 'end') {
      bottom = 0;
      top = step.signed;
      running = step.signed;
    } else {
      const next = running + step.signed;
      bottom = Math.min(running, next);
      top = Math.max(running, next);
      running = next;
    }

    return {
      name: step.name,
      bottom,
      top,
      value: Math.abs(top - bottom),
      signed: step.signed,
      kind: step.kind,
      fill: fillForStep(step),
      bridge: running,
    };
  });
}

function chartPeak(data) {
  return Math.max(...data.map((d) => Math.max(d.top, 0)), 1);
}

function yScaleFactory(yMax, plotTop, plotHeight) {
  return (value) => plotTop + plotHeight * (1 - value / yMax);
}

function WaterfallLegend() {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[7px] text-gray-500">
      <span className="inline-flex items-center gap-1">
        <span className="w-2 h-2 rounded-sm bg-emerald-600" /> Sales
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-2 h-2 rounded-sm bg-red-400" /> Cost section
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-2 h-2 rounded-sm bg-slate-400" /> PBT / Net
      </span>
    </div>
  );
}

function WaterfallSvg({ data, yMax, chart, currency, onHover }) {
  const plotLeft = chart.padLeft;
  const plotTop = chart.padTop;
  const plotWidth = chart.width - chart.padLeft - chart.padRight;
  const plotHeight = chart.height - chart.padTop - chart.padBottom;
  const toY = yScaleFactory(yMax, plotTop, plotHeight);

  const n = data.length || 1;
  const gap = plotWidth * 0.06;
  const barWidth = Math.max((plotWidth - gap * (n + 1)) / n, 8);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * yMax);

  return (
    <svg
      viewBox={`0 0 ${chart.width} ${chart.height}`}
      width="100%"
      height="100%"
      role="img"
      aria-label="P&L waterfall chart"
    >
      {ticks.map((tick) => {
        const y = toY(tick);
        return (
          <g key={`tick-${tick}`}>
            <line
              x1={plotLeft}
              x2={plotLeft + plotWidth}
              y1={y}
              y2={y}
              stroke="#e5e7eb"
              strokeDasharray="3 3"
            />
            <text
              x={plotLeft - 6}
              y={y + 3}
              textAnchor="end"
              fontSize={chart.tickSize}
              fill="#9ca3af"
            >
              {formatAxisTick(tick)}
            </text>
          </g>
        );
      })}

      {data.slice(0, -1).map((row, i) => {
        const x1 = plotLeft + gap + i * (barWidth + gap) + barWidth;
        const x2 = plotLeft + gap + (i + 1) * (barWidth + gap);
        const y = toY(row.bridge);
        return (
          <line
            key={`${row.name}-bridge`}
            x1={x1}
            y1={y}
            x2={x2}
            y2={y}
            stroke="#cbd5e1"
            strokeWidth={1.5}
            strokeDasharray="5 4"
          />
        );
      })}

      {data.map((row, i) => {
        const x = plotLeft + gap + i * (barWidth + gap);
        const yTop = toY(Math.max(row.top, row.bottom));
        const yBottom = toY(Math.min(row.top, row.bottom));
        const height = Math.max(yBottom - yTop, 1);
        const isDelta = row.kind === 'delta';
        const labelY = isDelta ? yBottom + chart.labelSize + 4 : yTop - 5;

        return (
          <g
            key={row.name}
            onMouseEnter={() => onHover?.(row)}
            onMouseLeave={() => onHover?.(null)}
          >
            <title>{`${row.name}: ${formatMoney(row.signed, currency)}`}</title>
            <rect
              x={x}
              y={yTop}
              width={barWidth}
              height={height}
              fill={row.fill}
              rx={2}
            />
            <text
              x={x + barWidth / 2}
              y={labelY}
              textAnchor="middle"
              fontSize={chart.labelSize}
              fontWeight={600}
              fill="#374151"
            >
              {formatWaterfallLabel(row.signed)}
            </text>
            <text
              x={x + barWidth / 2}
              y={chart.height - 10}
              textAnchor="middle"
              fontSize={chart.labelSize}
              fill="#6b7280"
              transform={`rotate(-28 ${x + barWidth / 2} ${chart.height - 10})`}
            >
              {row.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function WaterfallFullscreen({ data, yMax, currency, taxLabel, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      data-testid="waterfall-fullscreen-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="P&L waterfall fullscreen"
    >
      <div
        className="relative w-full max-w-[96vw] max-h-[92vh] bg-white rounded-lg shadow-2xl border border-slate-200 p-3 md:p-5 flex flex-col overflow-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="waterfall-fullscreen-panel"
      >
        <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
          <div>
            <div className="text-xs font-bold text-gray-700 uppercase tracking-wide">
              P&amp;L Waterfall ({currency})
            </div>
            <div className="text-[10px] text-gray-400">Sales → Net · {taxLabel}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="bg-white hover:bg-slate-100 border border-slate-300 rounded-full p-1.5 min-h-[32px] min-w-[32px] shadow-sm transition-colors"
            data-testid="waterfall-fullscreen-close"
            title="Close"
            aria-label="Close fullscreen waterfall"
          >
            <X weight="bold" className="w-4 h-4 text-slate-700" />
          </button>
        </div>

        <div
          className="w-full flex-1 min-h-[280px]"
          style={{ aspectRatio: `${CHART_FULL.width} / ${CHART_FULL.height}` }}
        >
          <WaterfallSvg
            data={data}
            yMax={yMax}
            chart={CHART_FULL}
            currency={currency}
          />
        </div>

        <div className="mt-2 shrink-0">
          <WaterfallLegend />
        </div>
      </div>
    </div>
  );
}

export function PlWaterfallChart({ outputs, currency = 'CHF', taxLabel = 'IBGI' }) {
  const uid = useId();
  const data = useMemo(() => buildWaterfallSteps(outputs, taxLabel), [outputs, taxLabel]);
  const yMax = useMemo(() => chartPeak(data), [data]);
  const [hover, setHover] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);

  return (
    <>
      <div
        className="bg-white border border-gray-200 rounded p-1.5 mt-1 relative"
        data-testid="pl-waterfall-chart"
      >
        <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between gap-1">
          <span>P&amp;L Waterfall ({currency})</span>
          <div className="flex items-center gap-1">
            <span className="normal-case font-medium text-gray-400">Sales → Net</span>
            <button
              type="button"
              onClick={() => setFullscreen(true)}
              className="bg-white hover:bg-slate-50 border border-slate-300 rounded-md p-1 min-h-[22px] min-w-[22px] shadow-sm transition-colors"
              data-testid="waterfall-fullscreen-btn"
              title="Expand waterfall"
              aria-label="Expand waterfall to fullscreen"
            >
              <ArrowsOut weight="bold" className="w-3 h-3 text-blue-600" />
            </button>
          </div>
        </div>

        <div
          className="relative w-full"
          style={{ aspectRatio: `${CHART_INLINE.width} / ${CHART_INLINE.height}` }}
        >
          <WaterfallSvg
            data={data}
            yMax={yMax}
            chart={CHART_INLINE}
            currency={currency}
            onHover={setHover}
          />

          {hover && (
            <div
              className="pointer-events-none absolute top-2 right-2 rounded border border-slate-200 bg-white px-2 py-1 shadow-sm text-[10px]"
              data-testid={`waterfall-tooltip-${uid}`}
            >
              <div className="font-semibold text-slate-700">{hover.name}</div>
              <div className="tabular-nums text-slate-600">{formatMoney(hover.signed, currency)}</div>
            </div>
          )}
        </div>

        <div className="mt-1">
          <WaterfallLegend />
        </div>
      </div>

      {fullscreen && (
        <WaterfallFullscreen
          data={data}
          yMax={yMax}
          currency={currency}
          taxLabel={taxLabel}
          onClose={() => setFullscreen(false)}
        />
      )}
    </>
  );
}

export { buildWaterfallSteps, formatWaterfallLabel, chartPeak };
